import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import { jwtDecode, type JwtPayload } from "jwt-decode";
import type { Moment } from "moment";
import moment from "moment/moment";
import { useEffect, useMemo, useState } from "react";
export type UseBearerTokenReturnValue = {
    accessToken: string | null;
    expiry: Moment | null;
    hasExpired: boolean;
    refresh: () => void;
    isLoading: boolean;
};
export function useBearerToken(): UseBearerTokenReturnValue {
    const [hasTokenExpired, setHasTokenExpired] = useState<boolean>(false);
    const { result, isLoading, refetch } = useInlineStatusQuery(async (repo) => await repo.Users.generateAccessToken(), [], "Generate access token for user");
    const expiry = useMemo(() => {
        if (!result) {
            return null;
        }
        const payload = jwtDecode<JwtPayload>(result.AccessToken);
        return getExpiry(payload);
    }, [result]);
    useEffect(() => {
        if (!result || !expiry) {
            return;
        }
        const checkIfExpired = () => {
            const hasExpired = expiry < moment();
            //if the expiry state has changed, update it
            if (hasExpired !== hasTokenExpired) {
                setHasTokenExpired(hasExpired);
            }
        };
        //do an initial check
        checkIfExpired();
        //schedule a time for every 5s
        const intervalId = setInterval(checkIfExpired, 5000 /* 5s */);
        return () => clearInterval(intervalId);
    }, [result, expiry, hasTokenExpired]);
    return {
        accessToken: result === null ? null : result.AccessToken,
        expiry,
        hasExpired: hasTokenExpired,
        refresh: refetch,
        isLoading,
    };
}
export function getExpiry(payload: JwtPayload): Moment | null {
    return payload.exp ? moment(payload.exp * 1000) : null;
}
